<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="flex h-full w-full flex-col">
    <div
      class="flex w-full items-center border-b border-solid border-b-gray-400 px-3 py-4 tablet:py-6"
    >
      <button
        class="flex h-8 w-8 basis-8 cursor-pointer items-center justify-center"
        title="search"
        @click="handleSubmit"
      >
        <IconSearch class="h-6 w-6 text-blue-400" />
      </button>
      <input
        ref="searchInputRef"
        v-model.trim="searchInputValue"
        type="search"
        autocomplete="off"
        class="w-full flex-1 text-sm"
        :placeholder="tGlobal('search-menu.placeholder')"
        @keyup.enter="handleSubmit"
      />
      <button
        class="flex h-8 w-8 basis-8 cursor-pointer items-center justify-center"
        title="close"
        @click="handleClose"
      >
        <IconClose class="h-6 w-6 text-gray-600" />
      </button>
    </div>
    <div class="w-full grow">
      <div class="h-full w-full overflow-hidden px-3">
        <div
          class="container-scrollbar h-full w-full overflow-y-auto overflow-x-hidden"
        >
          <div
            v-if="isLoading"
            class="flex h-24 w-full items-center justify-center"
          >
            <LoaderBounce />
          </div>
          <div
            v-else-if="!isShowPopularKeyword && searchResults.length <= 0"
            class="py-3"
          >
            <p class="text-base font-bold text-gray-600">
              {{ tGlobal('search-menu.no-results') }}
            </p>
          </div>
          <ul v-else-if="!isShowPopularKeyword && searchResults.length">
            <li v-for="item in searchResults" :key="item.title" class="py-3">
              <TheNuxtLink
                :to="item.pageUrl"
                :title="item.title"
                class="flex items-center text-base font-bold hover:text-blue-400"
              >
                <span
                  class="inline flex-1 truncate leading-6 tablet:flex-initial"
                  v-html="provideMarkerText(item.title, searchValue)"
                />
                <TheBadge
                  v-if="provideIsShowBadge(item.source)"
                  :name="provideSourceName(item.source)"
                  theme="light-blue"
                  class="ml-2 hidden flex-shrink-0 basis-0 whitespace-nowrap tablet:inline-block"
                />
                <IconCaret
                  direction="right"
                  class="ml-1 h-4 w-4 basis-4 text-black tablet:hidden"
                />
              </TheNuxtLink>
            </li>
            <li
              class="w-full border-solid border-gray-400 py-3 tablet:border-t"
            >
              <TheButton
                :to="`/${locale}/marketplace/search?keyword=${searchValue}&page=1`"
                :title="tGlobal('search-menu.see-all-results')"
                class="flex w-full items-center justify-center tablet:hidden"
              >
                <span class="flex-1 truncate text-sm">
                  {{ tGlobal('search-menu.see-all-results') }}
                </span>
                <IconCaret direction="right" class="ml-1 text-white" />
              </TheButton>
              <TheNuxtLink
                :to="`/${locale}/marketplace/search?keyword=${searchValue}&page=1`"
                :title="tGlobal('search-menu.see-all-results')"
                class="hidden text-base font-bold text-blue-400 tablet:inline-block"
              >
                {{ tGlobal('search-menu.see-all-results') }}
              </TheNuxtLink>
            </li>
          </ul>
          <ul v-else>
            <li
              v-for="item in popularKeywords"
              :key="item.searchKey"
              class="py-3"
            >
              <TheNuxtLink
                :to="`/${locale}/marketplace/search?keyword=${item.searchKey}&page=1`"
                :title="item.name"
                class="flex items-center text-base font-bold"
              >
                <span class="flex-1 truncate">{{ item.name }}</span>
                <IconCaret
                  direction="right"
                  class="ml-1 h-4 w-4 basis-4 text-black tablet:hidden"
                />
              </TheNuxtLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { refDebounced } from '@vueuse/core';

  import { EnumSource } from '@/assets/types/api/Search';
  import provideIsShowBadge from '@/components/Layout/Search/provideIsShowBadge';

  const { t, locale } = useI18n({ useScope: 'local' });
  const { t: tGlobal } = useI18n({ useScope: 'global' });

  const searchMenuStore = useSearchMenuStore();
  const { searchValue, searchResults, popularKeywords } =
    storeToRefs(searchMenuStore);

  const searchInputValue = ref<string>(searchValue.value);
  const debounced = refDebounced(searchInputValue, 600);

  const searchInputRef = ref<HTMLElement | null>(null);

  const { status, execute } = await searchMenuStore.USE_GET_SEARCH();

  const isLoading = computed((): boolean => !!(status.value === 'pending'));

  const isShowPopularKeyword = computed(
    (): boolean => searchValue.value === ''
  );

  const handleSubmit = (): void => {
    if (searchValue.value.trim()) {
      execute();
    }
  };

  const handleClose = (): void => {
    searchMenuStore.HANDLE_CLOSE_MENU();
  };

  const provideSourceName = (source: string): string => {
    switch (source) {
      case EnumSource.PRODUCTS:
        return t('products');
      case EnumSource.SOLUTION:
        return t('solutions');
      default:
        return source;
    }
  };

  watch(debounced, (value: string) => {
    searchValue.value = value;
  });

  watch(searchValue, () => handleSubmit());

  onMounted(() => {
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "en-jp": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "en-tw": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "en-kr": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "en-americas": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "en-asia-pacific": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "en-emea": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "ja-jp": {
    "products": "製品",
    "solutions": "ソリューション"
  },
  "zh-cn": {
    "products": "产品",
    "solutions": "解决方案"
  },
  "zh-tw": {
    "products": "產品",
    "solutions": "解決方案"
  }
}
</i18n>
